/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 20190902 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable } from '@angular/core';
import { TourStopsService } from '../tour-stop.service';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { BehaviorSubject } from 'rxjs';
import { RemoteActionService } from 'src/app/_services/remote-action/logic/remote-action.service';
import { AuthenticationService } from 'src/app/_services/authentication/authentication.service';
import { TourStopsDaoService } from '../../dao/tour-stop-dao.service';
import { IMSId } from 'src/app/_model/ims-object';
import { AppService } from 'src/app/_services/app-service/logic/app.service';
import { CredentialsService } from 'src/app/_services/credentials/logic/credentials.service';
import { LocationService } from 'src/app/_services/location/location.service';
import { TourStop, TourStopDTO } from 'src/app/_model/tour-stop';
import { InitializeStatus } from 'src/app/_services/initialize/initialize-aware';
import { BadgesService } from 'src/app/_services/badges/logic/badges.service';
import { APILevel } from 'src/app/_services/sync/api-level';


/**
 * Loads TourStops
 */
@Injectable()
export class TourStopsLogicService implements TourStopsService {
  public tourStops$ = new BehaviorSubject<TourStop[]>([]);
  public initializeStatus$: BehaviorSubject<InitializeStatus> = new BehaviorSubject(InitializeStatus.NOT_INITIALIZED);

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private remoteActionService: RemoteActionService,
    private tourStopsDaoService: TourStopsDaoService,
    private appService: AppService,
    private credentialService: CredentialsService,
    private locationService: LocationService,
    private badgesService: BadgesService
  ) {
    this.initialize();
  }

  // Public Function
  public getTourStopById(id: IMSId): TourStop {
    if (id === undefined) {
      return undefined
    }

    return this.tourStops$.getValue().find((tourStop) => {
      if (tourStop.id.imsId === id.imsId && tourStop.id.secondaryId === id.secondaryId) {
        return true;
      }
      return false;
    });
  }

  public getTourStopsByTourId(id: IMSId): TourStop[] {
    if (id === undefined) {
      return []
    }

    return this.tourStops$.getValue().filter((tourStop) => {
      if (tourStop.tour.id.imsId === id.imsId && tourStop.tour.id.secondaryId === id.secondaryId) {
        return true;
      }
      return false;
    });
  }

  public async updateTourStops(tourStopsDto: TourStopDTO[]): Promise<void> {
    this.logger.log(
      this.constructor.name,
      '- Received new tour stops from other IMS component',
      tourStopsDto?.length
    );

    try {
      let tourStops: TourStop[] = [];

      for (const tourStopDto of tourStopsDto) {
        tourStops.push(TourStop.fromDto(tourStopDto));
      }

      await this.tourStopsDaoService.saveTourStops(tourStops);
      await this.tourStops$.next(tourStops);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error parsing TourStops: ',
        error.message
      );
      throw error;
    }
  }

  public async updateTourStop(tourStop: TourStop) {
    let tourStops = this.tourStops$.getValue();
    for (let ts of tourStops) {
      if (ts.id.imsId === tourStop.id.imsId || ts.id.secondaryId === tourStop.id.secondaryId) {
        ts = tourStop;
      }
    }
    this.tourStops$.next(tourStops);
    await this.tourStopsDaoService.saveTourStops(tourStops);
  }

  // Resetable
  public async reset() {
    await this.tourStopsDaoService.reset();
    await this.loadLocalTourStops();
  }

  public async clear() {
    await this.tourStopsDaoService.clear();
    this.tourStops$.next([]);
  }

  // Syncable
  public async sync(apiLevel: APILevel, syncKey: string): Promise<void> { }

  public async presync(): Promise<void> { }

  public async postsync(): Promise<void> { }

  // InitializeAware
  async initialize(): Promise<void> {
    this.initializeStatus$.next(InitializeStatus.INITIALIZING)
    try {
      await this.loadLocalTourStops();
      this.initializeStatus$.next(InitializeStatus.INITIALIZED)
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local data: ',
        error?.message
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE)
    }
  }

  // Private Functions
  private async loadLocalTourStops() {
    try {
      this.logger.log(this.constructor.name, '- Load local TourStops');
      let tourStops: TourStop[] = await this.tourStopsDaoService.getTourStops();
      this.logger.log(
        this.constructor.name,
        '- Loaded local TourStops',
        tourStops.length
      );
      this.tourStops$.next(tourStops);
      await this.badgesService.clearBadgesFromNotAvailableTourStops(tourStops);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local tour stops: ',
        error.message
      );
      throw error;
    }
  }

  async initializeCompleted(): Promise<void> {

  }

}
